/* eslint-disable no-prototype-builtins */
import { IConfigRetailUser, IConfigStateGroups, Results, Subtasks, Task } from 'constants/interfaces'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { mapUsersByGroups } from 'redux/actions/config'
import { store } from 'config/redux'
import { countSubtaskStatuses, deriveTaskStatusValue, SubtaskStatusCounts } from 'utils/taskStatusModel'

// NOTE: the old `getTaskStatus({ subtasksLength, resultsLength, hasRejected, allApproved })`
// helper lived here and was a second, drifting copy of the status rule. It is gone — the single
// implementation is `deriveTaskStatus` in `utils/taskStatusModel`.

export const formatRelativeDateTime = (timestamp: number): string => {
  const { translations } = store.getState().config
  const { locale } = store.getState().general
  dayjs.extend(relativeTime)
  dayjs.extend(advancedFormat)

  const date = dayjs(timestamp * 1000)
  const now = dayjs()

  let relativeDate: string

  if (date.isSame(now, 'day')) {
    relativeDate = 't_today'
  } else if (date.isSame(now.subtract(1, 'day'), 'day')) {
    relativeDate = 't_yesterday'
  } else if (date.isSame(now.subtract(2, 'days'), 'day')) {
    relativeDate = 't_2_days_ago'
  } else if (date.isSame(now, 'week')) {
    relativeDate = 't_this_week'
  } else if (date.isSame(now, 'month')) {
    relativeDate = 't_this_month'
  } else if (date.isSame(now, 'year')) {
    relativeDate = 't_this_year'
  } else if (date.isSame(now.subtract(1, 'year'), 'year')) {
    relativeDate = 't_year_ago'
  } else {
    relativeDate = date.format('YYYY')
  }

  const time = date.format('HH:mm')

  return `${translations[locale][relativeDate]} ${translations[locale]?.['t_at'] ?? 'at'} ${time}`
}

type GetAvatarSrc = { id: string; groups: IConfigStateGroups; retailUsersObject: { [key: string]: IConfigRetailUser } }

export const getAvatarSrc = ({ id, groups, retailUsersObject }: GetAvatarSrc) => {
  if (retailUsersObject?.[id]) {
    return retailUsersObject?.[id]?.profile_img_url
  } else {
    const group = { [id]: groups[id] }
    const groupManagerId = mapUsersByGroups({ groups: group, retailUsersObject: retailUsersObject! })?.[id]?.[0] ?? ''
    return retailUsersObject?.[groupManagerId]?.profile_img_url
  }
}

/**
 * Per-subtask status counts for a task, from the current user's results in Redux.
 * The denominator is the task's subtask count, so subtasks with no result yet count as `todo`.
 * Feeds both `calculateTaskStatus` and the `StatusComposition` breakdown UI.
 */
export const calculateTaskStatusCounts = (task: Task, subtasks: Subtasks, results: Results): SubtaskStatusCounts => {
  const { tid } = task
  const subtasksLength = subtasks?.[tid]?.length ?? 0
  const taskResults = results?.[tid] ?? []

  return countSubtaskStatuses(
    taskResults.map((result) => result.status),
    subtasksLength
  )
}

/**
 * The task's status — one of the 5 values of the shared task status model
 * (`utils/taskStatusModel`). Approved-with-reservation and AI-reviewed subtasks fold into
 * APPROVED; they are no longer task statuses of their own.
 */
export const calculateTaskStatus = (task: Task, subtasks: Subtasks, results: Results) =>
  deriveTaskStatusValue(calculateTaskStatusCounts(task, subtasks, results))
